import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { ScrollArea } from '../ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { QrCode, Link, Mail, Phone, Wifi, MapPin, MessageSquare, X } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { QRCodeSVG } from 'qrcode.react';

const qrTypes = [
  { id: 'website', name: 'Website URL', icon: Link, placeholder: 'https://example.com' },
  { id: 'email', name: 'Email', icon: Mail, placeholder: 'hello@example.com' },
  { id: 'phone', name: 'Phone', icon: Phone, placeholder: '+1234567890' },
  { id: 'wifi', name: 'WiFi', icon: Wifi, placeholder: 'Network Name' },
  { id: 'location', name: 'Location', icon: MapPin, placeholder: 'Address or coordinates' },
  { id: 'text', name: 'Plain Text', icon: MessageSquare, placeholder: 'Enter your text' },
];

const qrDesigns = [
  { id: 'square', name: 'Square' },
  { id: 'rounded', name: 'Rounded' },
  { id: 'dots', name: 'Dots' },
];

const QRCodePanel = ({ onAddElement, onClose }) => {
  const { toast } = useToast();
  const [qrType, setQrType] = useState('website');
  const [qrValue, setQrValue] = useState('');
  const [qrColor, setQrColor] = useState('#000000');
  const [qrBgColor, setQrBgColor] = useState('#ffffff');
  const [qrDesign, setQrDesign] = useState('square');

  // WiFi specific fields
  const [wifiPassword, setWifiPassword] = useState('');
  const [wifiEncryption, setWifiEncryption] = useState('WPA');

  const getQRData = () => {
    switch (qrType) {
      case 'email':
        return `mailto:${qrValue}`;
      case 'phone':
        return `tel:${qrValue}`;
      case 'wifi':
        return `WIFI:T:${wifiEncryption};S:${qrValue};P:${wifiPassword};;`;
      case 'location':
        return `geo:${qrValue}`;
      default:
        return qrValue;
    }
  };

  const handleAddQRCode = () => {
    if (!qrValue.trim()) {
      toast({
        title: 'Enter a value',
        description: 'Please enter the QR code content',
        variant: 'destructive'
      });
      return;
    }

    // Create a canvas to capture the QR code as image
    const qrData = getQRData();
    
    // Add as a special QR code element
    onAddElement({
      type: 'qrcode',
      data: qrData,
      x: 100,
      y: 100,
      width: 200,
      height: 200,
      color: qrColor,
      bgColor: qrBgColor,
      design: qrDesign
    });

    toast({
      title: 'QR Code added',
      description: 'QR code has been added to your canvas'
    });
  };

  const currentType = qrTypes.find(t => t.id === qrType);
  const TypeIcon = currentType?.icon || Link;

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">QR Code Generator</h3>
            <p className="text-xs text-gray-500 mt-1">Create scannable QR codes</p>
          </div>
          {onClose && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {/* QR Type Selection */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">QR Code Type</Label>
            <div className="grid grid-cols-3 gap-2">
              {qrTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => {
                      setQrType(type.id);
                      setQrValue('');
                    }}
                    className={`p-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                      qrType === type.id
                        ? 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`qr-type-${type.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="text-[10px]">{type.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Input Field */}
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Content</Label>
            <div className="relative">
              <TypeIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder={currentType?.placeholder}
                value={qrValue}
                onChange={(e) => setQrValue(e.target.value)}
                className="pl-9"
                data-testid="qr-value-input"
              />
            </div>
          </div>

          {/* WiFi specific fields */}
          {qrType === 'wifi' && (
            <>
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Password</Label>
                <Input
                  type="password"
                  placeholder="WiFi Password"
                  value={wifiPassword}
                  onChange={(e) => setWifiPassword(e.target.value)}
                  data-testid="qr-wifi-password"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Encryption</Label>
                <Select value={wifiEncryption} onValueChange={setWifiEncryption}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="WPA">WPA/WPA2</SelectItem>
                    <SelectItem value="WEP">WEP</SelectItem>
                    <SelectItem value="nopass">None</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* Colors */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="text-xs font-semibold">QR Color</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={qrColor}
                  onChange={(e) => setQrColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border"
                />
                <Input
                  value={qrColor}
                  onChange={(e) => setQrColor(e.target.value)}
                  className="flex-1 h-10 text-xs"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Background</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={qrBgColor}
                  onChange={(e) => setQrBgColor(e.target.value)}
                  className="w-10 h-10 rounded cursor-pointer border"
                />
                <Input
                  value={qrBgColor}
                  onChange={(e) => setQrBgColor(e.target.value)}
                  className="flex-1 h-10 text-xs"
                />
              </div>
            </div>
          </div>

          {/* QR Preview */}
          {qrValue && (
            <div className="space-y-2">
              <Label className="text-xs font-semibold">Preview</Label>
              <div 
                className="flex items-center justify-center p-4 rounded-lg border"
                style={{ backgroundColor: qrBgColor }}
              >
                <QRCodeSVG
                  value={getQRData()}
                  size={120}
                  fgColor={qrColor}
                  bgColor={qrBgColor}
                  level="M"
                />
              </div>
            </div>
          )}

          {/* Add Button */}
          <Button 
            onClick={handleAddQRCode}
            className="w-full bg-purple-600 hover:bg-purple-700"
            disabled={!qrValue.trim()}
            data-testid="add-qr-btn"
          >
            <QrCode className="w-4 h-4 mr-2" />
            Add QR Code
          </Button>
        </div>
      </ScrollArea>
    </div>
  );
};

export default QRCodePanel;
